import { combineReducers } from 'redux';
import { RootActions } from 'types/state';
import { FormTypes, SF86 } from 'constants/formTypes';
import relationshipsReducer from './reducers/relationships';
import historyReducer from './reducers/history';
import errorReducer from './reducers/error';
import * as ActionTypes from './actionTypes';
import { FETCH_FORM_SUCCESS, FETCH_STATUS_SUCCESS } from '../api/actionTypes';
import { RootActionTypes } from '../actionTypes';
import { fetchFormSuccessSlice } from './slices/fetchForm';
import { getQualifiedFormType } from '../../../helpers/form';
import { FormDetails } from '../../../util/singletons';

export const initialState: ApplicationState = {
  Settings: {
    formType: SF86,
    hasInit: false,
    hash: '',
    formVersion: '',
    status: '',
    pdfExpiration: '',
    consented: '',
    mobileNavigation: false,
  },
  Identification: {},
  Financial: {},
  Relationships: {},
  Citizenship: {},
  Military: {},
  History: {},
  Foreign: {},
  TBD: {},
  Legal: {},
  Psychological: {},
  Substance: {},
  Package: {},
  Submission: {},
  Completed: {},
  Errors: {},
  AddressBooks: {},
};

export interface ApplicationState {
  Settings: ApplicationSettingsState
  Identification: ApplicationSectionState
  Financial: ApplicationSectionState
  Relationships: ApplicationSectionState
  Citizenship: ApplicationSectionState
  Military: ApplicationSectionState
  History: ApplicationSectionState
  Foreign: ApplicationSectionState
  TBD: ApplicationSectionState
  Legal: ApplicationSectionState
  Psychological: ApplicationSectionState
  Substance: ApplicationSectionState
  Package: ApplicationSectionState
  Submission: ApplicationSectionState
  Completed: ApplicationSectionState
  Errors: ApplicationSectionState
  AddressBooks: ApplicationSectionState
}

export interface ApplicationSettingsState {
  formType: FormTypes
  hasInit: boolean
  hash: string
  status: string
  formVersion: string
  pdfExpiration: string
  consented: string
  mobileNavigation: boolean
  acceptedTerms?: {
    name: string
    value: string
  }
}

export interface ApplicationSectionState {
  [key: string]: any
}

type ReducerTypes = ApplicationSectionState | ApplicationSettingsState

export const reducer = <ReducerTypes>(sectionName: string, defaultState: ReducerTypes) => (
  state: ReducerTypes = defaultState,
  action: RootActions
): ReducerTypes => {
  switch (action.type) {
    case FETCH_FORM_SUCCESS:
      // If specific settings react to this differently, we can either handle it
      // here or create a unique reducer for it.
      if (sectionName === 'Settings') {
        const formType = getQualifiedFormType(action.payload.formType, action.payload.formVersion);
        FormDetails.getInstance().setFormType(formType);
        FormDetails.getInstance().setFormVersion(action.payload.formVersion);
        return {
          ...state,
          formType,
          formVersion: action.payload.formVersion,
        };
      }

      return fetchFormSuccessSlice(state, action, sectionName);

    case FETCH_STATUS_SUCCESS:
      if (sectionName === 'Settings') {
        const formType = getQualifiedFormType(action.payload.formType, action.payload.formVersion);
        return {
          ...state,
          formType,
          formVersion: action.payload.formVersion,
          hash: action.payload.hash,
          status: action.payload.status,
          pdfExpiration: action.payload.pdfExpiration,
          hasInit: true,
        };
      }
      return state;

    case ActionTypes.UPDATE_APP:
      // Check that section matches intended section reducer. This is to prevent
      // merging of everything every time an action is dispatched. We only
      // perform for the relevant section
      if (action.section === sectionName) {
        // copy current state
        const updated = { ...state };

        // Override all values for the particular reducer key
        // @ts-ignore
        updated[action.property] = action.values;
        return updated;
      }

      return state;

    case RootActionTypes.authentication.LOGGED_OFF:
      return {
        ...defaultState,
      };

    default:
      return state;
  }
};

// High level pre-defined sub-state tree
const applicationReducer = combineReducers({
  Settings: reducer('Settings', initialState.Settings),
  Identification: reducer('Identification', initialState.Identification),
  Financial: reducer('Financial', initialState.Financial),
  Relationships: relationshipsReducer,
  Citizenship: reducer('Citizenship', initialState.Citizenship),
  Military: reducer('Military', initialState.Military),
  History: historyReducer,
  Foreign: reducer('Foreign', initialState.Foreign),
  TBD: reducer('Tbd', {}),
  Legal: reducer('Legal', initialState.Legal),
  Psychological: reducer('Psychological', initialState.Psychological),
  Substance: reducer('Substance', initialState.Substance),
  Package: reducer('Package', initialState.Package),
  Submission: reducer('Submission', initialState.Submission),
  Completed: errorReducer,
  Errors: errorReducer,
  AddressBooks: reducer('AddressBooks', initialState.AddressBooks),
});

export default applicationReducer;
